import React, { useEffect } from 'react';
import {
  PROCEDURE_CATEGORIES,
  GST_RATES,
  PROCEDURE_FLAG_HINTS,
} from '../../constants/procedures';
import InfoHint from '../common/InfoHint';
import useOrgSettingsStore from '../../stores/orgSettingsStore';
import './ProcedureFormRow.css';

/**
 * Inline create/edit form for a single ClinicProcedure entry.
 *
 * Props:
 *   data            — { name, category, ... }
 *   errors          — { [field]: 'error message' }
 *   onChange(field, value)
 *   onSave()
 *   onCancel()
 *   isEdit          — bool; when true, category is locked (backend-enforced)
 *                     and the `active` toggle appears
 *   isSubmitting    — bool, disables buttons during API calls
 *   departments     — A4/S2 optional: org ACTIVE departments [{dept_id, name}].
 *                     Omitted/empty → the Departments picker is hidden. Rendered
 *                     on BOTH create and edit forms, saved as part of the same
 *                     primary action (no standalone "Save departments" button).
 *   selectedDeptIds — currently-selected department ids for this form instance.
 *   onToggleDept(deptId) — toggles one department in the selection.
 */
function ProcedureFormRow({
  data, errors = {}, onChange, onSave, onCancel, isEdit = false, isSubmitting = false,
  departments = [], selectedDeptIds = [], onToggleDept,
}) {
  const isDental = data.category === 'dental';

  const field = (name) => errors[name];

  // When the clinic skips payment for ALL procedures, the per-procedure
  // "skip payment" choice is moot — hide it (matches backend precedence).
  const skipPaymentAll = useOrgSettingsStore((s) => s.skipPaymentAll);
  const fetchSettings = useOrgSettingsStore((s) => s.fetchProcedurePaymentSettings);
  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  return (
    <div className="procedure-form-row">
      <div className="procedure-form-grid">
        {/* Name */}
        <div className="pfr-field pfr-field-wide">
          <label>Name <span className="pfr-required">*</span></label>
          <input
            type="text"
            value={data.name || ''}
            onChange={(e) => onChange('name', e.target.value)}
            placeholder="e.g. Root Canal — Molar"
            maxLength={200}
          />
          {field('name') && <span className="pfr-error">{field('name')}</span>}
        </div>

        {/* Category — displayed as "Clinical type" (the speciality axis,
            distinct from "Visible to" department scoping below). */}
        <div className="pfr-field">
          <label>Clinical type <span className="pfr-required">*</span></label>
          <select
            value={data.category || ''}
            onChange={(e) => onChange('category', e.target.value)}
            disabled={isEdit}
            title={isEdit ? 'Clinical type is part of the DB key and cannot be changed' : ''}
          >
            <option value="">— pick —</option>
            {PROCEDURE_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          {field('category') && <span className="pfr-error">{field('category')}</span>}
          {isEdit && <span className="pfr-hint">Fixed on edit</span>}
        </div>

        {/* Default cost */}
        <div className="pfr-field">
          <label>Default Cost (₹) <span className="pfr-required">*</span></label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={data.default_cost ?? ''}
            onChange={(e) => onChange('default_cost', e.target.value === '' ? '' : parseFloat(e.target.value))}
          />
          {field('default_cost') && <span className="pfr-error">{field('default_cost')}</span>}
        </div>

        {/* GST */}
        <div className="pfr-field">
          <label>GST Rate</label>
          <select
            value={data.gst_rate ?? 0}
            onChange={(e) => onChange('gst_rate', parseInt(e.target.value, 10))}
          >
            {GST_RATES.map((r) => (
              <option key={r} value={r}>{r}%</option>
            ))}
          </select>
        </div>

        {/* Specialty */}
        <div className="pfr-field">
          <label>Specialty</label>
          <input
            type="text"
            value={data.specialty || ''}
            onChange={(e) => onChange('specialty', e.target.value)}
            placeholder="e.g. endodontics"
            maxLength={80}
          />
        </div>

        {/* ADA code (dental only) */}
        {isDental && (
          <div className="pfr-field">
            <label>ADA Code</label>
            <input
              type="text"
              value={data.ada_code || ''}
              onChange={(e) => onChange('ada_code', e.target.value)}
              placeholder="e.g. D3330"
              maxLength={10}
            />
          </div>
        )}

        {/* Duration */}
        <div className="pfr-field">
          <label>Duration (min)</label>
          <input
            type="number"
            min="0"
            max="600"
            value={data.typical_duration_minutes ?? ''}
            onChange={(e) =>
              onChange(
                'typical_duration_minutes',
                e.target.value === '' ? null : parseInt(e.target.value, 10)
              )
            }
          />
        </div>

        {/* Notes */}
        <div className="pfr-field pfr-field-wide">
          <label>Notes</label>
          <textarea
            value={data.notes || ''}
            onChange={(e) => onChange('notes', e.target.value)}
            placeholder="Optional notes for clinical staff"
            maxLength={500}
            rows={2}
          />
        </div>

        {/* Flags */}
        <div className="pfr-flags pfr-field-wide">
          <label>
            <input
              type="checkbox"
              checked={!!data.is_tooth_specific}
              disabled={!isDental}
              onChange={(e) => onChange('is_tooth_specific', e.target.checked)}
            />
            Tooth-specific
            {!isDental && <span className="pfr-hint"> (dental only)</span>}
            {' '}<InfoHint text={PROCEDURE_FLAG_HINTS.is_tooth_specific} />
          </label>
          <label>
            <input
              type="checkbox"
              checked={!!data.nurse_allowed}
              onChange={(e) => onChange('nurse_allowed', e.target.checked)}
            />
            Nurse-allowed
            {' '}<InfoHint text={PROCEDURE_FLAG_HINTS.nurse_allowed} />
          </label>
          <label>
            <input
              type="checkbox"
              checked={!!data.receptionist_allowed}
              onChange={(e) => onChange('receptionist_allowed', e.target.checked)}
            />
            Receptionist-allowed
            {' '}<InfoHint text={PROCEDURE_FLAG_HINTS.receptionist_allowed} />
          </label>
          <label>
            <input
              type="checkbox"
              // When the clinic skips ALL procedures, this per-procedure
              // choice is moot — show it checked + disabled so it's clearly
              // governed by the global setting (never just missing).
              checked={skipPaymentAll ? true : !!data.skip_payment}
              disabled={skipPaymentAll}
              onChange={(e) => onChange('skip_payment', e.target.checked)}
            />
            Skip payment
            {' '}<InfoHint text={PROCEDURE_FLAG_HINTS.skip_payment} />
            {skipPaymentAll && (
              <span className="pfr-hint"> (on for all procedures)</span>
            )}
          </label>
          <label>
            <input
              type="checkbox"
              checked={!!data.one_time_only}
              onChange={(e) => onChange('one_time_only', e.target.checked)}
            />
            One-time only
            {' '}<InfoHint text={PROCEDURE_FLAG_HINTS.one_time_only} />
          </label>
          {!!data.one_time_only && (
            <div className="pfr-field" style={{ marginTop: '0.35rem', width: '100%' }}>
              <input
                type="text"
                maxLength={300}
                placeholder="Reason shown to the doctor (optional) — e.g. dating scan, done once"
                value={data.one_time_only_reason || ''}
                onChange={(e) => onChange('one_time_only_reason', e.target.value)}
              />
            </div>
          )}
          {isEdit && (
            <label>
              <input
                type="checkbox"
                checked={data.active !== false}
                onChange={(e) => onChange('active', e.target.checked)}
              />
              Active
            </label>
          )}
        </div>

        {/* A4/S2 — Departments (optional, multi). ONLY a plain multi-select —
            no 3-way scope-mode selector (that's A5). Hidden when the org has
            no active departments. Empty selection = org-wide (implicit).
            Saved as part of the same Save/Create click as everything else. */}
        {departments.length > 0 && (
          <div className="pfr-depts pfr-field-wide">
            <label className="pfr-depts-title">
              Visible to{' '}
              <span className="pfr-hint">(optional, multi — leave empty for org-wide)</span>
            </label>
            <div className="pfr-depts-options">
              {departments.map((d) => (
                <label key={d.dept_id} className="pfr-dept-option">
                  <input
                    type="checkbox"
                    checked={selectedDeptIds.includes(d.dept_id)}
                    onChange={() => onToggleDept && onToggleDept(d.dept_id)}
                  />
                  {d.name}
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="procedure-form-actions">
        <button
          type="button"
          className="pfr-btn pfr-btn-secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="pfr-btn pfr-btn-primary"
          onClick={onSave}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create'}
        </button>
      </div>
    </div>
  );
}

export default ProcedureFormRow;
